/**
 * AI Agent Interface
 */
export interface AIAgent {
  id: string;
  name: string;
  type: string;
  description: string;
  status: 'active' | 'inactive' | 'learning' | 'error' | 'maintenance';
  version: string;
  lastActive: string;
  tasksCompleted: number;
  successRate: number;
  avgResponseTime: string;
  cpuUsage: number;
  memoryUsage: number;
  category: string;
  icon: string;
  color: string;
  tags: string[];
}

/**
 * AI Agents Data - Centralized data source for all agents
 */
export const AI_AGENTS_DATA: AIAgent[] = [
  {
    id: 'agent-001',
    name: 'OCR Agent',
    type: 'Document Processing',
    description: 'Advanced Optical Character Recognition for extracting text from images, PDFs, and scanned documents with high accuracy.',
    status: 'active',
    version: 'v2.1.4',
    lastActive: '2 minutes ago',
    tasksCompleted: 1247,
    successRate: 98.5,
    avgResponseTime: '2.1s',
    cpuUsage: 45,
    memoryUsage: 62,
    category: 'document',
    icon: '👁️',
    color: 'from-blue-500 to-blue-600',
    tags: ['OCR', 'Text Extraction', 'Image Processing']
  },
  {
    id: 'agent-002',
    name: 'Customer Support Agent',
    type: 'Support AI',
    description: 'Intelligent customer service automation with natural language processing, sentiment analysis, and multi-language support.',
    status: 'active',
    version: 'v2.3.1',
    lastActive: '30 seconds ago',
    tasksCompleted: 3421,
    successRate: 95.7,
    avgResponseTime: '0.9s',
    cpuUsage: 65,
    memoryUsage: 71,
    category: 'support',
    icon: '💬',
    color: 'from-teal-500 to-teal-600',
    tags: ['NLP', 'Sentiment Analysis', 'Chatbot']
  },
  {
    id: 'agent-003',
    name: 'Compliance Audit Agent',
    type: 'Compliance AI',
    description: 'Automated regulatory compliance monitoring, audit trail generation, and risk assessment for financial services.',
    status: 'active',
    version: 'v2.2.0',
    lastActive: '1 minute ago',
    tasksCompleted: 987,
    successRate: 97.8,
    avgResponseTime: '1.6s',
    cpuUsage: 33,
    memoryUsage: 45,
    category: 'compliance',
    icon: '⚖️',
    color: 'from-green-500 to-green-600',
    tags: ['Compliance', 'Audit', 'Risk Assessment']
  },
  {
    id: 'agent-004',
    name: 'Fraud Detection Agent',
    type: 'Fraud Prevention',
    description: 'Real-time fraud detection using machine learning algorithms to identify suspicious transactions and patterns.',
    status: 'active',
    version: 'v3.0.2',
    lastActive: '15 seconds ago',
    tasksCompleted: 2156,
    successRate: 99.2,
    avgResponseTime: '0.3s',
    cpuUsage: 78,
    memoryUsage: 85,
    category: 'fraud',
    icon: '🛡️',
    color: 'from-red-500 to-red-600',
    tags: ['Fraud Detection', 'ML', 'Real-time Analysis']
  },
  {
    id: 'agent-005',
    name: 'Claims Processing Agent',
    type: 'Claims AI',
    description: 'Automated claims processing with document analysis, damage assessment, and settlement recommendations.',
    status: 'active',
    version: 'v2.5.1',
    lastActive: '45 seconds ago',
    tasksCompleted: 1890,
    successRate: 94.3,
    avgResponseTime: '3.2s',
    cpuUsage: 52,
    memoryUsage: 68,
    category: 'claims',
    icon: '📋',
    color: 'from-purple-500 to-purple-600',
    tags: ['Claims', 'Document Analysis', 'Settlement']
  },
  {
    id: 'agent-006',
    name: 'Risk Assessment Agent',
    type: 'Risk Management',
    description: 'Comprehensive risk evaluation for insurance underwriting with predictive analytics and market analysis.',
    status: 'active',
    version: 'v2.4.3',
    lastActive: '1 minute ago',
    tasksCompleted: 1456,
    successRate: 96.8,
    avgResponseTime: '2.8s',
    cpuUsage: 41,
    memoryUsage: 55,
    category: 'risk',
    icon: '📊',
    color: 'from-orange-500 to-orange-600',
    tags: ['Risk Assessment', 'Underwriting', 'Predictive Analytics']
  },
  {
    id: 'agent-007',
    name: 'Analytics Dashboard Agent',
    type: 'Business Intelligence',
    description: 'Real-time business intelligence with automated reporting, trend analysis, and performance metrics.',
    status: 'active',
    version: 'v2.1.8',
    lastActive: '2 minutes ago',
    tasksCompleted: 876,
    successRate: 98.1,
    avgResponseTime: '1.2s',
    cpuUsage: 28,
    memoryUsage: 42,
    category: 'analytics',
    icon: '📈',
    color: 'from-indigo-500 to-indigo-600',
    tags: ['Analytics', 'Reporting', 'Business Intelligence']
  },
  {
    id: 'agent-008',
    name: 'Report Generation Agent',
    type: 'Documentation AI',
    description: 'Automated report generation with data visualization, chart creation, and executive summary compilation.',
    status: 'active',
    version: 'v2.3.5',
    lastActive: '3 minutes ago',
    tasksCompleted: 654,
    successRate: 97.5,
    avgResponseTime: '4.1s',
    cpuUsage: 35,
    memoryUsage: 48,
    category: 'reports',
    icon: '📄',
    color: 'from-cyan-500 to-cyan-600',
    tags: ['Report Generation', 'Data Visualization', 'Documentation']
  },
  {
    id: 'agent-009',
    name: 'Underwriting Assistant',
    type: 'Underwriting AI',
    description: 'Intelligent underwriting support with policy analysis, risk scoring, and premium calculation.',
    status: 'active',
    version: 'v2.2.7',
    lastActive: '1 minute ago',
    tasksCompleted: 1123,
    successRate: 95.9,
    avgResponseTime: '2.5s',
    cpuUsage: 38,
    memoryUsage: 51,
    category: 'underwriting',
    icon: '📝',
    color: 'from-emerald-500 to-emerald-600',
    tags: ['Underwriting', 'Policy Analysis', 'Risk Scoring']
  },
  {
    id: 'agent-010',
    name: 'Customer Insights Agent',
    type: 'Customer Analytics',
    description: 'Customer behavior analysis with segmentation, churn prediction, and personalized recommendations.',
    status: 'active',
    version: 'v2.4.1',
    lastActive: '30 seconds ago',
    tasksCompleted: 2341,
    successRate: 93.7,
    avgResponseTime: '1.8s',
    cpuUsage: 47,
    memoryUsage: 59,
    category: 'customer',
    icon: '👥',
    color: 'from-pink-500 to-pink-600',
    tags: ['Customer Analytics', 'Segmentation', 'Churn Prediction']
  }
];

/**
 * Get agents by category
 */
export const getAgentsByCategory = (category: string): AIAgent[] => {
  return AI_AGENTS_DATA.filter(agent => agent.category === category);
};

/**
 * Get active agents
 */
export const getActiveAgents = (): AIAgent[] => {
  return AI_AGENTS_DATA.filter(agent => agent.status === 'active');
};

/**
 * Get agent by ID
 */
export const getAgentById = (id: string): AIAgent | undefined => {
  return AI_AGENTS_DATA.find(agent => agent.id === id);
};

/**
 * Get total tasks completed by all agents
 */
export const getTotalTasksCompleted = (): number => {
  return AI_AGENTS_DATA.reduce((total, agent) => total + agent.tasksCompleted, 0);
};

/**
 * Get average success rate across all agents
 */
export const getAverageSuccessRate = (): number => {
  const totalSuccessRate = AI_AGENTS_DATA.reduce((total, agent) => total + agent.successRate, 0);
  return totalSuccessRate / AI_AGENTS_DATA.length;
};





